import React, { useCallback, useMemo } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import { useDashboard } from '../../contexts/DashboardContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSettings } from '../../contexts/SettingsContext';
import WidgetRenderer from './WidgetRenderer';
import WidgetContainer from './WidgetContainer';
import {
  deriveZitiusAiCollapsedLayout,
  doubleLayoutDimensions,
} from '../../utils/zitiusAiDashboardLayout';
import 'react-grid-layout/css/styles.css';

const ResponsiveGridLayout = WidthProvider(GridLayout);

const GridDashboard = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const {
    currentContext,
    layout,
    widgetConfigs,
    isEditMode,
    handleLayoutChange,
    addWidget,
    removeWidget,
  } = useDashboard();

  const zitiusCollapsedOverlay =
    !isEditMode &&
    currentContext?.context_key === 'zitius_ai_overview' &&
    settings.zitiusAiUserContentCollapsed;

  const ZITIUS_RAIL_GRID_COLS = 24;

  const displayLayout = useMemo(() => {
    if (isEditMode) return layout;
    if (currentContext?.context_key !== 'zitius_ai_overview') return layout;
    if (!settings.zitiusAiUserContentCollapsed) return layout;
    const doubled = doubleLayoutDimensions(layout);
    return deriveZitiusAiCollapsedLayout(doubled, true, { totalCols: ZITIUS_RAIL_GRID_COLS });
  }, [layout, isEditMode, currentContext?.context_key, settings.zitiusAiUserContentCollapsed]);

  const handleLayoutChangeSafe = useCallback(
    (newLayout) => {
      // Do not persist react-grid-layout updates while showing derived collapsed layout,
      // or the saved dashboard layout would be overwritten with collapsed coordinates.
      if (zitiusCollapsedOverlay) return;
      handleLayoutChange(newLayout);
    },
    [zitiusCollapsedOverlay, handleLayoutChange]
  );

  const handleDropDragOver = useCallback((e) => {
    if (!isEditMode) return null;
    const size = window.currentDragData?.defaultSize || { w: 4, h: 3 };
    return { w: size.w, h: size.h };
  }, [isEditMode]);

  const handleDrop = useCallback((currentLayout, layoutItem, event) => {
    if (!isEditMode) return;
    let dragData = null;
    try {
      const raw = event.dataTransfer?.getData('text/plain');
      dragData = raw ? JSON.parse(raw) : window.currentDragData;
    } catch {
      dragData = window.currentDragData;
    }
    if (dragData?.widgetKey && layoutItem) {
      addWidget(dragData.widgetKey, {
        x: layoutItem.x,
        y: layoutItem.y,
        w: layoutItem.w,
        h: layoutItem.h,
      });
    }
  }, [isEditMode, addWidget]);

  if (displayLayout.length === 0) {
    return (
      <div className='citius-dashboard-grid w-full flex flex-col items-center justify-center h-64 text-center'>
        <p className='text-muted-foreground text-lg mb-2'>No widgets on this dashboard</p>
        <p className='text-muted-foreground text-sm'>{t('dashboard.editModeHint')}</p>
      </div>
    );
  }

  return (
    <div className='citius-dashboard-grid w-full'>
    <ResponsiveGridLayout
      layout={displayLayout}
      cols={zitiusCollapsedOverlay ? ZITIUS_RAIL_GRID_COLS : 12}
      rowHeight={zitiusCollapsedOverlay ? 30 : 60}
      margin={[16, 16]}
      isDraggable={isEditMode}
      isResizable={isEditMode}
      isDroppable={isEditMode}
      draggableHandle='.widget-drag-handle'
      compactType={zitiusCollapsedOverlay ? null : 'vertical'}
      onLayoutChange={handleLayoutChangeSafe}
      onDropDragOver={handleDropDragOver}
      onDrop={handleDrop}
    >
      {displayLayout.map(item => {
        const config = widgetConfigs[item.i];
        if (!config) return null;

        return (
          <div key={item.i}>
            <WidgetContainer
              widgetKey={item.i}
              isEditMode={isEditMode}
              isLocked={config.is_locked}
              onRemove={() => removeWidget(item.i)}
            >
              <WidgetRenderer
                widget={{ widget_key: item.i, ...config }}
              />
            </WidgetContainer>
          </div>
        );
      })}
    </ResponsiveGridLayout>
    </div>
  );
};

export default GridDashboard;
